import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { get } from 'lodash';

import EyeIcon from '../../assets/icons/eye';
import BuyIcon from '../../components/balance-header/icons/buy-icon.svg';
import DocumentViewModal from '../../components/document-view-modal';
import { CONST_OPERATION_TYPE, TRANSACTION_TYPE_MAP } from '../../constants';
import DetailsContainer from '../../containers/details-container';
import GoBack from '../../containers/go-back';
import AppPageMain from '../../modules/app-page/app-page--main';
import IconBtn from '../../modules/icon-btn/icon-btn';
import PageHeader from '../../modules/page-header';
import Tooltip from '../../modules/tooltip';
import { useAppDispatch } from '../../store';
import { getDocument, getDocumentPDF } from '../../store/actions/documents';
import { RootState } from '../../types';
import currencyFormat from '../../utils/currencyFormat';
import { downloadFile, getPdfFromBase64 } from '../../utils/files/downloadFile';
import { getFormatDate } from '../../utils/utils';

import './index.scss';

const DocumentDetailedPage: FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isOpenModal, setOpenModal] = useState(false);
  const [documentFile, setDocumentFile] = useState<string>();
  const { id, type } = history.location.state;
  const document = useSelector(
    (state: RootState) => state.documents.data.document
  );

  useEffect(() => {
    dispatch(getDocument({ id, type }));
  }, [id]);

  const rightDetailsList = useMemo(
    () => [
      [
        {
          title: 'Тип файла документа:',
          value: `Adobe .PDF   ${document?.weight || 0}Мб`,
        },
        {
          title: 'Тип подписи:',
          value: (
            <div className="flex">
              УКЭП .SIG
              <Tooltip
                title={
                  <>
                    Вы можете проверить подпись документа на сайте{' '}
                    <a
                      href="https://crypto.kontur.ru/verify"
                      target="_blank"
                      rel="noreferrer"
                    >
                      crypto.kontur.ru
                    </a>
                  </>
                }
                placement="top-end"
                isCloseClick
              >
                <div>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M9.99996 18.3333C14.6023 18.3333 18.3333 14.6023 18.3333 9.99996C18.3333 5.39759 14.6023 1.66663 9.99996 1.66663C5.39759 1.66663 1.66663 5.39759 1.66663 9.99996C1.66663 14.6023 5.39759 18.3333 9.99996 18.3333Z"
                      stroke="#96B1FE"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.5 7.50377C7.69592 6.94683 8.08263 6.47719 8.59163 6.17805C9.10063 5.8789 9.69908 5.76955 10.281 5.86936C10.8629 5.96918 11.3907 6.27171 11.7709 6.72338C12.1511 7.17505 12.3592 7.74671 12.3583 8.3371C12.3583 10.0038 10 10.8334 10 10.8334V12.0834"
                      stroke="#96B1FE"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 14.1666H10.0083"
                      stroke="#96B1FE"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Как проверить подпись
                </div>
              </Tooltip>
            </div>
          ),
        },
        {
          title: 'Создатель:',
          value: get(document, 'creator', '-'),
        },
        {
          title: 'Контрагент:',
          value: get(document, 'agent', '-'),
        },
      ],
    ],
    [document]
  );

  const downloadDocument = useCallback(
    (sig?: boolean, isLoad?: boolean) => {
      if (id)
        dispatch(
          getDocumentPDF({
            id,
            type,
            signed: !!sig,
          })
        ).then((res) => {
          if (res.payload) {
            const resultBlob = getPdfFromBase64(
              (res.payload as { doc: string }).doc
            );
            if (!sig && !documentFile)
              setDocumentFile((res.payload as { doc: string }).doc);

            if (!isLoad)
              downloadFile(
                resultBlob,
                `${document?.filename}${
                  sig ? document?.extension + '.sig' : ''
                }`,
                'application/pdf'
              );
          }
        });
    },
    [id, document]
  );

  const getLink = useCallback(
    (transactionType, id) => {
      switch (transactionType) {
        case CONST_OPERATION_TYPE.CREDIT:
          return `/credit/details/${id}`;
        case CONST_OPERATION_TYPE.CREDIT_REPAYMENT:
          return `/credit/details/${id}`;
        case CONST_OPERATION_TYPE.PURCHASE:
          return `/assets/sign-info/${id}`;
        case CONST_OPERATION_TYPE.SALE:
          return `/assets/selling-sign-info/${id}`;
        case CONST_OPERATION_TYPE.ISSUE:
          return `/assets/cfa-info/${id}`;
        case CONST_OPERATION_TYPE.ISSUE_PAYMENT:
          return `/assets/cfa-info/${id}`;
        default:
          break;
      }
    },
    [id]
  );

  return (
    <AppPageMain>
      <div className="container document">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[
                {
                  link: '/documents',
                  title: 'Документы',
                },
                { link: '#', title: '' },
              ]}
              iconType="documents"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <DetailsContainer
              title="Документ"
              onClose={() => history.push('/documents')}
              infoTitle={{
                description: get(document, 'filename', '-'),
              }}
              leftComponent={
                <div className="details__col">
                  <h2 className="text-2 details__title">
                    Привязка документа к операции:
                  </h2>
                  {document?.linkedOperations.map((link) => (
                    <div className="document-item-container">
                      <p
                        className="link"
                        onClick={() =>
                          history.replace(
                            `${getLink(
                              link.transactionType,
                              link.operationId
                            )}`,
                            { id: link.operationId }
                          )
                        }
                      >
                        {(link.transactionType &&
                          TRANSACTION_TYPE_MAP[link.transactionType]) ||
                          '-'}
                      </p>
                      <p>{link.agent}</p>
                      <p>{currencyFormat(link.value)}</p>
                      <p>{getFormatDate(link.date, undefined, true)}</p>
                    </div>
                  ))}
                </div>
              }
              rightContent={{
                title: 'Информация по документу:',
                detailsList: rightDetailsList,
              }}
              rightBottomComponent={
                <div className="document-history">
                  <div className="document-history__header table__header-item">
                    История изменений документа
                  </div>
                  {document?.changes.map((change) => (
                    <div className="document-item-container">
                      <p>
                        {change.change} / {change.initiator}
                      </p>
                      <p>{getFormatDate(change.date, undefined, true)}</p>
                    </div>
                  ))}
                </div>
              }
              footer={
                <>
                  <div className="footer__go-back">
                    <GoBack onClick={() => history.push('/documents')} />
                  </div>
                  <div className="flex">
                    <IconBtn
                      onClick={() => {
                        downloadDocument(false, true);
                        setOpenModal(true);
                      }}
                      lg
                      icon={<EyeIcon />}
                      title="Посмотреть документ"
                    />
                    <IconBtn
                      onClick={() => downloadDocument()}
                      lg
                      icon={BuyIcon}
                      title="Скачать документ .PDF"
                    />
                    <IconBtn
                      onClick={() => downloadDocument(true)}
                      lg
                      icon={BuyIcon}
                      title="Скачать подпись .SIG"
                    />
                  </div>
                  <DocumentViewModal
                    file={documentFile}
                    isOpen={isOpenModal}
                    onClose={() => setOpenModal(false)}
                  />
                </>
              }
            />
          </div>
        </div>
      </div>
    </AppPageMain>
  );
};

export default DocumentDetailedPage;
